"use client";

import React, { useState, useRef, useMemo, ChangeEvent, DragEvent } from "react";
import {
  X,
  UploadCloud,
  FileSpreadsheet,
  Download,
  CheckCircle2,
  AlertCircle,
  ShieldCheck,
  ChevronRight,
  Database,
  ArrowRight,
  HelpCircle,
  Info,
  ArrowDownToLine,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  WorkspaceConfig,
  generateWorkspaceSampleCsv,
  getDefaultWorkspaceConfig,
} from "@/lib/workspacePresets";
import { downloadCsvFile } from "@/lib/csvDownload";

interface ImportLeadsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  targetTenantId?: number | null;
  targetTenantName?: string;
  workspaceConfig?: WorkspaceConfig;
}

interface ParsedLeadRow {
  name: string;
  email?: string;
  phone?: string;
  company_name?: string;
  service_type?: string;
  quantity?: number | string;
  unit?: string;
  estimated_deal_value?: number | string;
  currency?: string;
  priority?: string;
  status?: string;
  country_or_city?: string;
  notes?: string;
  [key: string]: any;
}

// RFC 4180 compliant CSV parser with UTF-8 BOM stripping & delimiter detection
function parseCsvText(text: string): { headers: string[]; rows: Record<string, string>[] } {
  // 1. Strip UTF-8 BOM if present (frequently added by Microsoft Excel)
  if (text.charCodeAt(0) === 0xfeff) {
    text = text.slice(1);
  }

  // 2. Auto-detect delimiter: comma (default), semicolon, or tab
  const firstLine = text.split(/\r\n|\r|\n/)[0] || "";
  let delimiter = ",";
  if (!firstLine.includes(",") && firstLine.includes(";")) {
    delimiter = ";";
  } else if (!firstLine.includes(",") && firstLine.includes("\t")) {
    delimiter = "\t";
  }

  const lines: string[][] = [];
  let currentRow: string[] = [];
  let currentField = "";
  let insideQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const nextChar = text[i + 1];

    if (char === '"') {
      if (insideQuotes && nextChar === '"') {
        currentField += '"';
        i++; // skip escaped quote
      } else {
        insideQuotes = !insideQuotes;
      }
    } else if (char === delimiter && !insideQuotes) {
      currentRow.push(currentField.trim());
      currentField = "";
    } else if ((char === "\r" || char === "\n") && !insideQuotes) {
      if (char === "\r" && nextChar === "\n") {
        i++; // skip CRLF
      }
      currentRow.push(currentField.trim());
      currentField = "";
      if (currentRow.some((f) => f.length > 0)) {
        lines.push(currentRow);
      }
      currentRow = [];
    } else {
      currentField += char;
    }
  }

  if (currentField.length > 0 || currentRow.length > 0) {
    currentRow.push(currentField.trim());
    if (currentRow.some((f) => f.length > 0)) {
      lines.push(currentRow);
    }
  }

  if (lines.length === 0) return { headers: [], rows: [] };

  const rawHeaders = lines[0].map((h) => h.replace(/^["'\uFEFF]|["'\uFEFF]$/g, "").trim());
  const rows: Record<string, string>[] = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (line.length === 0 || line.every((c) => !c)) continue;
    const rowObj: Record<string, string> = {};
    rawHeaders.forEach((h, colIndex) => {
      rowObj[h] = line[colIndex] || "";
    });
    rows.push(rowObj);
  }

  return { headers: rawHeaders, rows };
}

function cleanKey(str: string): string {
  return str.toLowerCase().replace(/[^a-z0-9]/g, "");
}

// Map user headers to standard lead fields with multi-industry & trading support + dynamic custom fields
function mapRowToLead(raw: Record<string, string>): ParsedLeadRow {
  const rawKeys = Object.keys(raw);
  const matchedRawKeys = new Set<string>();

  const getField = (keys: string[]): string => {
    // 1. Exact match on cleaned alphanumeric key
    for (const rawKey of rawKeys) {
      if (matchedRawKeys.has(rawKey)) continue;
      const cleanedRaw = cleanKey(rawKey);
      for (const key of keys) {
        if (cleanedRaw === cleanKey(key)) {
          if (raw[rawKey]?.trim()) {
            matchedRawKeys.add(rawKey);
            return raw[rawKey].trim();
          }
        }
      }
    }

    // 2. Tokenized match (split header on spaces, slashes, dashes, underscores, ampersands)
    // e.g. "Name / Contact Person" -> ["name", "contact", "person"]
    for (const rawKey of rawKeys) {
      if (matchedRawKeys.has(rawKey)) continue;
      const rawTokens = rawKey
        .toLowerCase()
        .split(/[\s/\\_&,-]+/)
        .map(cleanKey)
        .filter(Boolean);
      for (const key of keys) {
        const cleanTarget = cleanKey(key);
        if (rawTokens.includes(cleanTarget)) {
          if (raw[rawKey]?.trim()) {
            matchedRawKeys.add(rawKey);
            return raw[rawKey].trim();
          }
        }
      }
    }

    // 3. Substring / contains match
    for (const rawKey of rawKeys) {
      if (matchedRawKeys.has(rawKey)) continue;
      const cleanedRaw = cleanKey(rawKey);
      for (const key of keys) {
        const cleanTarget = cleanKey(key);
        if (cleanTarget.length >= 3 && (cleanedRaw.includes(cleanTarget) || cleanTarget.includes(cleanedRaw))) {
          if (raw[rawKey]?.trim()) {
            matchedRawKeys.add(rawKey);
            return raw[rawKey].trim();
          }
        }
      }
    }

    return "";
  };

  let name = getField(["name", "fullname", "contactname", "client", "leadname", "prospect", "buyer", "buyername", "contactperson", "person"]);
  let company_name = getField(["company", "companyname", "organization", "business", "firm", "tradingfirm", "trader", "enterprise"]);
  let email = getField(["email", "mail", "emailaddress"]);
  let phone = getField(["phone", "mobile", "whatsapp", "contact", "cell", "phonenumber", "telephone", "tel"]);
  let service_type = getField(["commodity", "product", "item", "material", "service", "servicetype", "interest", "category"]);
  let quantity = getField(["quantity", "qty", "volume", "lots", "pieces", "count", "mt", "tonnage"]);
  let unit = getField(["unit", "uom", "metric", "measure"]);
  let estimated_deal_value = getField(["tradevalue", "ordervalue", "dealvalue", "value", "amount", "budget", "estimateddealvalue", "price", "totalvalue", "rate"]);
  let currency = getField(["currency", "curr"]);
  let priority = getField(["priority", "leadpriority", "urgency"]);
  let status = getField(["status", "stage", "pipelinestage", "leadstatus", "inquirystage"]);
  let country_or_city = getField(["port", "destination", "destinationport", "city", "location", "country", "state", "region"]);
  let notes = getField(["incoterms", "terms", "notes", "remarks", "projectdetails", "details", "comment", "description"]);

  // Value-based heuristic fallbacks if missing
  const values = Object.values(raw).map((v) => (v || "").trim());

  // Email heuristic: any cell with '@' and '.'
  if (!email) {
    const foundEmail = values.find((v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v));
    if (foundEmail) email = foundEmail;
  }

  // Phone heuristic: any cell with 8-15 digits
  if (!phone) {
    const foundPhone = values.find((v) => {
      const digits = v.replace(/\D/g, "");
      return digits.length >= 8 && digits.length <= 15 && (/^\+/.test(v) || /^[0-9]/.test(v));
    });
    if (foundPhone) phone = foundPhone;
  }

  // Name heuristic: if name is missing but company exists, use company; or first non-empty text column
  if (!name) {
    if (company_name) {
      name = company_name;
    } else {
      const candidate = values.find((v) => v && v !== email && v !== phone && v.length >= 2 && !/^\d+(\.\d+)?$/.test(v));
      if (candidate) name = candidate;
    }
  }

  if (!company_name && name) {
    company_name = name;
  }

  // Capture all unmatched custom columns into dynamic custom_fields
  const custom_fields: Record<string, string> = {};
  for (const rawKey of rawKeys) {
    if (!matchedRawKeys.has(rawKey)) {
      const val = (raw[rawKey] || "").trim();
      if (val) {
        const normKey = rawKey.toLowerCase().startsWith("custom_")
          ? rawKey.toLowerCase()
          : `custom_${rawKey.toLowerCase().replace(/[^a-z0-9]/g, "_")}`;
        custom_fields[normKey] = val;
      }
    }
  }

  return {
    name,
    email,
    phone,
    company_name,
    service_type,
    quantity,
    unit,
    estimated_deal_value,
    currency,
    priority,
    status,
    country_or_city,
    notes,
    custom_fields: Object.keys(custom_fields).length > 0 ? custom_fields : undefined,
  };
}

export default function ImportLeadsModal({
  isOpen,
  onClose,
  onSuccess,
  targetTenantId,
  targetTenantName,
  workspaceConfig,
}: ImportLeadsModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [parsedLeads, setParsedLeads] = useState<ParsedLeadRow[]>([]);
  const [rawHeaders, setRawHeaders] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [importProgress, setImportProgress] = useState<string | null>(null);

  // Derive dynamic configuration for this workspace
  const activeConfig = workspaceConfig || getDefaultWorkspaceConfig(targetTenantId || 1);
  const templateInfo = useMemo(() => {
    return generateWorkspaceSampleCsv(activeConfig, targetTenantName);
  }, [activeConfig, targetTenantName]);

  const availableStages = activeConfig.pipelineStages && activeConfig.pipelineStages.length > 0
    ? activeConfig.pipelineStages
    : [{ id: "Stage1", name: "Inquiry / RFQ" }];

  // Settings
  const [duplicateMode, setDuplicateMode] = useState<"skip" | "update">("skip");
  const [defaultCurrency, setDefaultCurrency] = useState("INR");
  const [defaultStatus, setDefaultStatus] = useState(
    activeConfig.pipelineStages?.[0]?.name || "Inquiry / RFQ"
  );
  const [defaultService, setDefaultService] = useState(
    activeConfig.fields?.find((f) => f.key === "service_type")?.placeholder?.replace(/^e\.g\.\s*/, "") || "Goods / Services"
  );

  // Post-import state
  const [importResult, setImportResult] = useState<{
    success: boolean;
    importedCount: number;
    updatedCount: number;
    skippedCount: number;
    totalSubmitted: number;
    errors?: string[];
  } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Single reliable client-side template download strictly tailored to the workspace configuration
  const handleDownloadWorkspaceTemplate = () => {
    downloadCsvFile(
      templateInfo.filename,
      templateInfo.csvContent,
      `/api/crm/leads/sample-csv?userId=${targetTenantId || ""}`
    );
  };

  const processFile = (selectedFile: File) => {
    if (!selectedFile.name.toLowerCase().endsWith(".csv")) {
      setErrorMsg("Please upload a standard CSV (.csv) file format.");
      return;
    }

    setErrorMsg(null);
    setFile(selectedFile);
    setImportResult(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = (e.target?.result as string) || "";
      const { headers, rows } = parseCsvText(content);

      if (rows.length === 0) {
        setErrorMsg("The uploaded CSV file contains no data rows.");
        setParsedLeads([]);
        return;
      }

      setRawHeaders(headers);
      const mapped = rows.map(mapRowToLead);
      setParsedLeads(mapped);
    };
    reader.onerror = () => {
      setErrorMsg("Failed to read the selected file. Please verify file integrity.");
    };
    reader.readAsText(selectedFile);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleImportSubmit = async () => {
    if (parsedLeads.length === 0) {
      setErrorMsg("No valid leads detected for upload.");
      return;
    }

    setIsProcessing(true);
    setErrorMsg(null);
    setImportProgress(null);

    try {
      const BATCH_SIZE = 500;
      let totalImported = 0;
      let totalUpdated = 0;
      let totalSkipped = 0;
      let allErrors: string[] = [];

      for (let i = 0; i < parsedLeads.length; i += BATCH_SIZE) {
        const chunk = parsedLeads.slice(i, i + BATCH_SIZE);
        const batchNum = Math.floor(i / BATCH_SIZE) + 1;
        const totalBatches = Math.ceil(parsedLeads.length / BATCH_SIZE);

        if (totalBatches > 1) {
          setImportProgress(
            `Importing batch ${batchNum} of ${totalBatches} (${Math.min(i + BATCH_SIZE, parsedLeads.length).toLocaleString()} of ${parsedLeads.length.toLocaleString()} leads)...`
          );
        }

        const res = await fetch("/api/crm/leads/import", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            leads: chunk,
            skip_duplicates: duplicateMode === "skip",
            update_existing: duplicateMode === "update",
            default_service_type: defaultService,
            default_currency: defaultCurrency,
            default_status: defaultStatus,
            tenant_id: targetTenantId || undefined,
          }),
        });

        const data = await res.json();

        if (!res.ok || !data.success) {
          throw new Error(data.error || `Failed to import batch ${batchNum}.`);
        }

        totalImported += data.importedCount || 0;
        totalUpdated += data.updatedCount || 0;
        totalSkipped += data.skippedCount || 0;
        if (data.errors && data.errors.length) {
          allErrors = [...allErrors, ...data.errors];
        }
      }

      setImportResult({
        success: true,
        importedCount: totalImported,
        updatedCount: totalUpdated,
        skippedCount: totalSkipped,
        totalSubmitted: parsedLeads.length,
        errors: allErrors.slice(0, 10),
      });

      // Call success callback to refresh pipeline
      onSuccess();
    } catch (err: any) {
      setErrorMsg(err.message || "An unexpected error occurred during import.");
    } finally {
      setIsProcessing(false);
      setImportProgress(null);
    }
  };

  const resetImport = () => {
    setFile(null);
    setParsedLeads([]);
    setRawHeaders([]);
    setImportResult(null);
    setErrorMsg(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/75 backdrop-blur-md animate-in fade-in duration-200">
      <div className="relative w-full max-w-3xl max-h-[92vh] flex flex-col bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 shrink-0 bg-slate-50/70 dark:bg-slate-900/70 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-cyan-500 to-blue-600 flex items-center justify-center text-white shadow-md shadow-cyan-500/20">
              <FileSpreadsheet className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                Import Leads from CSV
                {targetTenantName && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border border-cyan-500/20">
                    Workspace: {targetTenantName}
                  </span>
                )}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Bulk upload contacts. Records are strictly isolated to your private console.
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Dynamic Profile-Driven CSV Template Download Banner (Single Option) */}
          <div className="p-4 rounded-2xl bg-cyan-500/5 border border-cyan-500/20 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-600 dark:text-cyan-400 shrink-0 mt-0.5">
                <FileSpreadsheet className="w-4 h-4" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="text-xs font-bold text-cyan-950 dark:text-cyan-200">
                    {templateInfo.profileName} CSV Template
                  </h4>
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border border-cyan-500/20">
                    Tailored for your Workspace
                  </span>
                </div>
                <p className="text-[11px] text-cyan-800/80 dark:text-cyan-300/80 mt-1">
                  Configured headers: <code className="font-mono bg-cyan-500/10 px-1 py-0.5 rounded text-[10px]">{templateInfo.visibleLabels.slice(0, 5).join(", ")}{templateInfo.visibleLabels.length > 5 ? ` +${templateInfo.visibleLabels.length - 5} more` : ""}</code>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={handleDownloadWorkspaceTemplate}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary hover:bg-primary/90 text-white text-xs font-bold shadow-md shadow-primary/20 transition-all cursor-pointer shrink-0"
                title={`Download CSV template matching your ${templateInfo.profileName} table headers and pipeline stages`}
              >
                <Download className="w-4 h-4" />
                <span>Download Template (.csv)</span>
              </button>
            </div>
          </div>

          {/* Success Result View */}
          {importResult ? (
            <div className="space-y-4 py-4 text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-500">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <div className="space-y-1">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                  Batch Import Complete!
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {importResult.importedCount} new lead(s) added, {importResult.updatedCount || 0} updated in your private workspace.
                </p>
              </div>

              {/* Stats badges */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 max-w-lg mx-auto pt-2">
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-800">
                  <div className="text-xl font-black text-emerald-600 dark:text-emerald-400">
                    {importResult.importedCount}
                  </div>
                  <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">New Leads</div>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-800">
                  <div className="text-xl font-black text-cyan-600 dark:text-cyan-400">
                    {importResult.updatedCount || 0}
                  </div>
                  <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Updated</div>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-800">
                  <div className="text-xl font-black text-amber-600 dark:text-amber-400">
                    {importResult.skippedCount}
                  </div>
                  <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Skipped</div>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-800">
                  <div className="text-xl font-black text-slate-800 dark:text-slate-200">
                    {importResult.totalSubmitted}
                  </div>
                  <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Total Rows</div>
                </div>
              </div>

              {/* Informative banner when leads were already present */}
              {importResult.importedCount === 0 && (importResult.updatedCount || 0) === 0 && importResult.skippedCount > 0 && (
                <div className="max-w-lg mx-auto p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-900 dark:text-amber-200 text-xs text-left space-y-1">
                  <div className="font-bold flex items-center gap-1.5">
                    <Info className="w-4 h-4 text-amber-500 shrink-0" />
                    <span>All {importResult.skippedCount} contacts already exist in your workspace pipeline</span>
                  </div>
                  <p className="text-[11px] text-amber-800/90 dark:text-amber-300/90">
                    Because &ldquo;Skip duplicates&rdquo; was selected, existing contact records were preserved. If you want to refresh their deal values, pipeline stage, or custom fields with this CSV, click &ldquo;Import Another File&rdquo; and select &ldquo;Update Existing Leads&rdquo;.
                  </p>
                </div>
              )}

              {importResult.errors && importResult.errors.length > 0 && (
                <div className="max-w-md mx-auto p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-left space-y-1 text-[11px] text-amber-700 dark:text-amber-300">
                  <div className="font-bold flex items-center gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5" />
                    Notice regarding skipped rows:
                  </div>
                  <ul className="list-disc list-inside space-y-0.5 text-[10px]">
                    {importResult.errors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="pt-4 flex items-center justify-center gap-3">
                <Button variant="outline" size="sm" onClick={resetImport}>
                  Import Another File
                </Button>
                <Button variant="primary" size="sm" onClick={onClose}>
                  Done &amp; View Pipeline
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* File Dropzone */}
              {!file ? (
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-3xl p-8 sm:p-10 text-center cursor-pointer transition-all ${
                    isDragging
                      ? "border-cyan-500 bg-cyan-500/10 scale-[1.01]"
                      : "border-slate-300 dark:border-slate-700 hover:border-cyan-500/60 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 flex items-center justify-center mb-3">
                    <UploadCloud className="w-7 h-7" />
                  </div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-800 dark:text-white">
                    Choose a CSV file or drag &amp; drop it here
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-sm mx-auto">
                    Supports Microsoft Excel, Google Sheets, and CRM exports saved in standard comma-separated (.csv) format.
                  </p>
                  <span className="inline-block mt-4 text-xs font-semibold px-3 py-1.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                    Browse Computer (.csv)
                  </span>
                </div>
              ) : (
                /* Selected File Card & Settings */
                <div className="space-y-4">
                  <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-800 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center shrink-0">
                        <FileSpreadsheet className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-slate-900 dark:text-white truncate">
                          {file.name}
                        </div>
                        <div className="text-[11px] text-slate-500 dark:text-slate-400">
                          {(file.size / 1024).toFixed(1)} KB · {parsedLeads.length} contact records identified
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={resetImport}
                      className="text-xs font-semibold text-red-500 hover:text-red-600 px-2 py-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                    >
                      Change File
                    </button>
                  </div>

                  {/* Import Configuration Settings */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 rounded-2xl bg-slate-50/50 dark:bg-slate-800/40 border border-slate-200/80 dark:border-slate-800">
                    <div>
                      <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 block mb-1">
                        Default Initial Stage
                      </label>
                      <select
                        value={defaultStatus}
                        onChange={(e) => setDefaultStatus(e.target.value)}
                        className="w-full text-xs font-medium bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-2.5 py-1.5 focus:outline-none focus:border-cyan-500"
                      >
                        {availableStages.map((stg) => (
                          <option key={stg.id || stg.name} value={stg.name}>
                            {stg.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 block mb-1">
                        Default Currency
                      </label>
                      <select
                        value={defaultCurrency}
                        onChange={(e) => setDefaultCurrency(e.target.value)}
                        className="w-full text-xs font-medium bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-2.5 py-1.5 focus:outline-none focus:border-cyan-500"
                      >
                        <option value="INR">INR (₹)</option>
                        <option value="AED">AED (د.إ)</option>
                        <option value="USD">USD ($)</option>
                        <option value="EUR">EUR (€)</option>
                        <option value="GBP">GBP (£)</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 block mb-1">
                        Default Service / Product
                      </label>
                      <input
                        type="text"
                        value={defaultService}
                        onChange={(e) => setDefaultService(e.target.value)}
                        placeholder="e.g. Lead Management"
                        className="w-full text-xs font-medium bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-2.5 py-1.5 focus:outline-none focus:border-cyan-500"
                      />
                    </div>
                  </div>

                  {/* Duplicate Handling Strategy */}
                  <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800 space-y-2">
                    <div className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                      Duplicate Handling Strategy:
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                      <label className={`flex items-start gap-2.5 p-2.5 rounded-xl border cursor-pointer transition-colors ${
                        duplicateMode === "skip"
                          ? "bg-cyan-500/10 border-cyan-500/40 text-cyan-950 dark:text-cyan-200"
                          : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400"
                      }`}>
                        <input
                          type="radio"
                          name="duplicateMode"
                          value="skip"
                          checked={duplicateMode === "skip"}
                          onChange={() => setDuplicateMode("skip")}
                          className="mt-0.5 text-cyan-600 focus:ring-cyan-500"
                        />
                        <div>
                          <div className="font-semibold text-xs text-slate-900 dark:text-white">Skip Duplicates</div>
                          <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">Keep existing leads and current pipeline stages untouched.</div>
                        </div>
                      </label>

                      <label className={`flex items-start gap-2.5 p-2.5 rounded-xl border cursor-pointer transition-colors ${
                        duplicateMode === "update"
                          ? "bg-cyan-500/10 border-cyan-500/40 text-cyan-950 dark:text-cyan-200"
                          : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400"
                      }`}>
                        <input
                          type="radio"
                          name="duplicateMode"
                          value="update"
                          checked={duplicateMode === "update"}
                          onChange={() => setDuplicateMode("update")}
                          className="mt-0.5 text-cyan-600 focus:ring-cyan-500"
                        />
                        <div>
                          <div className="font-semibold text-xs text-slate-900 dark:text-white">Update Existing Leads</div>
                          <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">Merge latest deal values, stages, notes & custom fields.</div>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Data Preview Table */}
                  {parsedLeads.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 px-1">
                        <span>Previewing first {Math.min(5, parsedLeads.length)} of {parsedLeads.length} rows:</span>
                        <span className="text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                          <ShieldCheck className="w-3.5 h-3.5" /> Private Tenant Encrypted
                        </span>
                      </div>
                      <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-slate-800 max-h-48">
                        <table className="w-full text-left text-xs border-collapse">
                          <thead className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 uppercase tracking-wider text-[10px]">
                            <tr>
                              <th className="py-2 px-3 font-bold">#</th>
                              <th className="py-2 px-3 font-bold">Name</th>
                              <th className="py-2 px-3 font-bold">Phone</th>
                              <th className="py-2 px-3 font-bold">Company</th>
                              <th className="py-2 px-3 font-bold">Deal Value</th>
                              <th className="py-2 px-3 font-bold">City</th>
                              <th className="py-2 px-3 font-bold">Custom Fields</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-normal">
                            {parsedLeads.slice(0, 5).map((row, idx) => (
                              <tr key={idx} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/40">
                                <td className="py-1.5 px-3 text-slate-400 font-mono text-[10px]">{idx + 1}</td>
                                <td className="py-1.5 px-3 font-semibold text-slate-900 dark:text-white">
                                  {row.name || <span className="text-red-400 italic">Missing</span>}
                                </td>
                                <td className="py-1.5 px-3 text-slate-600 dark:text-slate-300 font-mono">
                                  {row.phone || <span className="text-slate-400">-</span>}
                                </td>
                                <td className="py-1.5 px-3 text-slate-600 dark:text-slate-300">
                                  {row.company_name || <span className="text-slate-400">-</span>}
                                </td>
                                <td className="py-1.5 px-3 font-mono text-cyan-600 dark:text-cyan-400 font-semibold">
                                  {row.estimated_deal_value ? `${row.currency || defaultCurrency} ${row.estimated_deal_value}` : "-"}
                                </td>
                                <td className="py-1.5 px-3 text-slate-500">
                                  {row.country_or_city || "Bangalore"}
                                </td>
                                <td className="py-1.5 px-3">
                                  {row.custom_fields && Object.keys(row.custom_fields).length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {Object.entries(row.custom_fields).slice(0, 2).map(([k, v]) => (
                                        <span key={k} className="px-1.5 py-0.5 rounded bg-cyan-500/10 border border-cyan-500/20 text-cyan-600 dark:text-cyan-400 text-[10px] font-mono">
                                          {k.replace(/^custom_/, "")}: {String(v)}
                                        </span>
                                      ))}
                                      {Object.keys(row.custom_fields).length > 2 && (
                                        <span className="text-[10px] text-slate-400">+{Object.keys(row.custom_fields).length - 2}</span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-slate-400 text-[10px]">-</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Error Message */}
          {errorMsg && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        {!importResult && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/70 shrink-0">
            <button
              onClick={onClose}
              disabled={isProcessing}
              className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors cursor-pointer"
            >
              Cancel
            </button>

            <Button
              onClick={handleImportSubmit}
              disabled={!file || parsedLeads.length === 0 || isProcessing}
              variant="primary"
              size="sm"
              className="cursor-pointer"
            >
              {isProcessing ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>{importProgress || `Importing ${parsedLeads.length} leads...`}</span>
                </>
              ) : (
                <>
                  <ArrowDownToLine className="w-4 h-4" />
                  <span>Confirm &amp; Import {parsedLeads.length > 0 ? `(${parsedLeads.length.toLocaleString()})` : ""}</span>
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
